











































































































import { Component } from 'vue-property-decorator'
import axios from 'axios'
import VueBase from '@/pages/vue-base'
import {
  apiV1AdminWebauthnCredentialsPath,
  optionsApiV1AdminWebauthnCredentialsPath,
  apiV1AdminWebauthnCredentialPath
} from '@/rails/routes'
import {
  isWebAuthnSupported,
  prepareCreationOptions,
  serializeAttestationCredential,
  signalAllAcceptedCredentialsIfSupported
} from '@/utils/webauthn'

interface WebauthnCredentialItem {
  id: number
  external_id: string
  nickname: string
  created_at: string
  last_used_at: string | null
}

@Component
export default class AdminPasskeys extends VueBase {
  credentials: Array<WebauthnCredentialItem> = []
  webauthnUserId = ''
  rpId = window.location.hostname
  webauthnSupported = false

  registerDialog = false
  registerAttachment: 'platform' | 'cross-platform' = 'platform'
  registerNickname = ''
  registerPassword = ''
  showRegisterPassword = false

  deleteDialog = false
  deleteTarget: WebauthnCredentialItem | null = null
  deletePassword = ''
  showDeletePassword = false

  processing = false

  async mounted(): Promise<void> {
    this.setBreadcrumbs([
      { text: 'パスキー設定', disabled: true }
    ])
    this.webauthnSupported = isWebAuthnSupported()
    await this.fetchCredentials()
  }

  formatDate(value: string): string {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    const pad = (n: number): string => String(n).padStart(2, '0')
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  async fetchCredentials(): Promise<void> {
    this.showLoading()
    try {
      const res = await axios.get(apiV1AdminWebauthnCredentialsPath())
      this.credentials = res.data.credentials || []
      this.webauthnUserId = res.data.user_id || res.data.webauthn_user_id || ''
      this.rpId = res.data.rp_id || window.location.hostname
      if (this.webauthnSupported && this.webauthnUserId) {
        await signalAllAcceptedCredentialsIfSupported(
          this.rpId,
          this.webauthnUserId,
          this.credentials.map(c => c.external_id)
        )
      }
    } catch {
      this.showError('パスキー情報の取得に失敗しました')
    } finally {
      this.hideLoading()
    }
  }

  openRegisterDialog(attachment: 'platform' | 'cross-platform'): void {
    this.registerAttachment = attachment
    this.registerNickname = ''
    this.registerPassword = ''
    this.showRegisterPassword = false
    this.registerDialog = true
  }

  async registerPasskey(): Promise<void> {
    if (!this.registerPassword) {
      this.showError('現在のパスワードを入力してください')
      return
    }
    if (this.processing) return
    this.processing = true
    try {
      const optionsRes = await axios.post(optionsApiV1AdminWebauthnCredentialsPath(), {
        current_password: this.registerPassword,
        authenticator_attachment: this.registerAttachment,
        attachment: this.registerAttachment
      })
      const rawOptions = optionsRes.data.webauthn_options || optionsRes.data
      const publicKey = prepareCreationOptions(rawOptions)
      const credential = (await navigator.credentials.create({
        publicKey
      })) as PublicKeyCredential | null

      if (!credential) {
        this.showError('パスキーの登録がキャンセルされました')
        return
      }

      const serialized = serializeAttestationCredential(credential)
      await axios.post(apiV1AdminWebauthnCredentialsPath(), {
        current_password: this.registerPassword,
        nickname: this.registerNickname,
        credential: serialized,
        challenge_token: optionsRes.data.challenge_token
      })

      this.registerDialog = false
      this.showMessage('パスキーを登録しました')
      await this.fetchCredentials()
    } catch (e) {
      if (e?.name === 'InvalidStateError') {
        this.showError('この端末のパスキーは既に登録されています')
      } else if (e?.name === 'NotAllowedError') {
        this.showError('パスキーの登録がキャンセルされたか、タイムアウトしました')
      } else {
        const errMsg = e?.response?.data?.error || 'パスキーの登録に失敗しました'
        this.showError(errMsg)
      }
    } finally {
      this.processing = false
    }
  }

  openDeleteDialog(item: WebauthnCredentialItem): void {
    this.deleteTarget = item
    this.deletePassword = ''
    this.showDeletePassword = false
    this.deleteDialog = true
  }

  async confirmDeletePasskey(): Promise<void> {
    if (!this.deleteTarget) return
    if (!this.deletePassword) {
      this.showError('現在のパスワードを入力してください')
      return
    }
    if (this.processing) return
    this.processing = true
    try {
      await axios.delete(apiV1AdminWebauthnCredentialPath(this.deleteTarget.id), {
        data: {
          current_password: this.deletePassword
        }
      })
      this.deleteDialog = false
      this.showMessage('パスキーを削除しました')
      await this.fetchCredentials()
    } catch (e) {
      const errMsg = e?.response?.data?.error || 'パスキーの削除に失敗しました'
      this.showError(errMsg)
    } finally {
      this.processing = false
    }
  }
}
